import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createFridge, getFridge } from "@/lib/api";
import { getDisplayName, setDisplayName } from "@/lib/device";

export default function Group() {
  const navigate = useNavigate();
  const [name, setName] = useState(getDisplayName());
  const [fridgeName, setFridgeName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name.trim()) return toast("Pop your name in first.");
    setBusy(true);
    try {
      setDisplayName(name.trim());
      const f = await createFridge(fridgeName.trim(), name.trim());
      navigate(`/fridge/${f.code}`);
    } catch {
      toast("Couldn't open the fridge. Try again.");
      setBusy(false);
    }
  };

  const join = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return toast("Enter a fridge code.");
    setBusy(true);
    try {
      await getFridge(c);
      if (name.trim()) setDisplayName(name.trim());
      navigate(`/fridge/${c}`);
    } catch {
      toast("No fridge with that code.");
      setBusy(false);
    }
  };

  return (
    <div className="app-shell min-h-[100dvh] px-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-[calc(env(safe-area-inset-bottom)+3rem)]" data-testid="group-screen">
      <div className="flex items-center justify-between mb-8">
        <button data-testid="group-back-btn" onClick={() => navigate("/")} className="w-9 h-9 rounded-full glass flex items-center justify-center">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62]">Shared fridge</span>
        <div className="w-9" />
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 mb-5">
          <Users className="w-4 h-4 text-[#4a5c62]" strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#4a5c62]">Flatmate mode</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
          One fridge.
          <br />
          Many suspects.
        </h1>
        <p className="text-base text-[#4a5c62] mt-4">
          Open a shared fridge, send the link, and let the accusations fly both ways.
        </p>
      </motion.div>

      <div className="mt-8">
        <label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62]">Your name</label>
        <input
          data-testid="group-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Sam"
          maxLength={24}
          className="w-full glass rounded-sm px-4 py-3 mt-2 outline-none focus:ring-2 focus:ring-[#16242a] placeholder:text-[#7c8f95]"
        />
      </div>

      {/* Create */}
      <div className="glass rounded-sm p-5 mt-6">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62]">Open a new fridge</p>
        <input
          data-testid="group-fridge-name-input"
          value={fridgeName}
          onChange={(e) => setFridgeName(e.target.value)}
          placeholder="Fridge name (optional) e.g. Flat 3B"
          maxLength={40}
          className="w-full bg-white/60 hairline rounded-sm px-4 py-3 mt-3 outline-none focus:ring-2 focus:ring-[#16242a] placeholder:text-[#7c8f95]"
        />
        <button
          data-testid="group-create-btn"
          onClick={create}
          disabled={busy}
          className="btn-ink w-full rounded-sm py-4 mt-4 flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-5 h-5 spin-slow" /> : null}
          Create & get a link <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Join */}
      <div className="glass rounded-sm p-5 mt-4">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62]">Join with a code</p>
        <div className="flex gap-2 mt-3">
          <input
            data-testid="group-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="AB12"
            maxLength={6}
            className="flex-1 bg-white/60 hairline rounded-sm px-4 py-3 outline-none focus:ring-2 focus:ring-[#16242a] font-mono tracking-[0.3em] uppercase placeholder:text-[#7c8f95] placeholder:tracking-normal"
          />
          <button data-testid="group-join-btn" onClick={join} disabled={busy} className="btn-ink rounded-sm px-6 disabled:opacity-60">
            Join
          </button>
        </div>
      </div>
    </div>
  );
}
